import React, { type ErrorInfo, type ReactNode } from 'react';

import { sendError } from '../utils/errors';

type AppsignalErrorBoundaryProps = {
  children: ReactNode;
  componentName: string;
  resetKey: string | number;
  rootElement?: Element | null;
  source: 'live-react' | 'react-renderer';
};

type AppsignalErrorBoundaryState = {
  hasError: boolean;
};

export default class AppsignalErrorBoundary extends React.Component<
  AppsignalErrorBoundaryProps,
  AppsignalErrorBoundaryState
> {
  override state: AppsignalErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): AppsignalErrorBoundaryState {
    return {
      hasError: true,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { componentName, rootElement, source } = this.props;

    sendError(error, {
      action: 'ReactErrorBoundary#render',
      tags: {
        component: componentName,
        source,
      },
      params: {
        component_name: componentName,
        component_stack: errorInfo.componentStack,
        root_element_id: rootElement instanceof HTMLElement ? rootElement.id : undefined,
        root_element_tag: rootElement?.tagName?.toLowerCase(),
        source,
      },
      breadcrumb: {
        category: 'react',
        action: 'render-error',
        metadata: {
          component: componentName,
          source,
        },
      },
    });
  }

  override componentDidUpdate(prevProps: AppsignalErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  override render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
